import React, { useContext, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Cropper from 'react-easy-crop';
import { API, AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Upload, RefreshCw, Trash2, UserRound, ZoomIn, Camera, Sparkles, Loader2 } from 'lucide-react';

// Center-crop the selected image to the chosen 1:1 pixel box; cap at 800px for
// quality-vs-size balance suitable for company-ID production. Returns a canvas.
async function cropToSquareCanvas(src, cropPixels) {
  const img = document.createElement('img');
  img.src = src;
  await new Promise((res) => { img.onload = res; });
  const size = Math.min(800, Math.round(cropPixels.width));
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, size, size);
  ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, size, size);
  return canvas;
}
const canvasToBlob = (c) => new Promise((res) => c.toBlob((b) => res(b), 'image/jpeg', 0.92));
const dataUrlToBlob = async (u) => (await fetch(u)).blob();

/** Square headshot with crop/preview/replace/remove. */
export const PersonnelPhotoField = ({ personnelId, hasPhoto = false, readOnly = false, onChange, size = 'lg' }) => {
  const { token } = useContext(AuthContext);
  const inputRef = useRef(null);
  const camRef = useRef(null);
  const [present, setPresent] = useState(hasPhoto);
  const [bust, setBust] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [rawSrc, setRawSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [origUrl, setOrigUrl] = useState(null);   // plain cropped square (data URL)
  const [cleanUrl, setCleanUrl] = useState(null);  // white-background version (data URL)
  const [useClean, setUseClean] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);

  const base = `${API}/personnel/${personnelId}/photo`;
  const src = `${base}?auth=${token}&t=${bust}`;
  const box = size === 'sm' ? 'h-10 w-10' : 'h-28 w-28';

  const resetCropState = () => { setRawSrc(null); setOrigUrl(null); setCleanUrl(null); setUseClean(false); };

  const pick = () => inputRef.current?.click();
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image exceeds 8MB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCrop({ x: 0, y: 0 }); setZoom(1); setOrigUrl(null); setCleanUrl(null); setUseClean(false); };
    reader.readAsDataURL(file);
  };
  const onCropComplete = useCallback((_area, px) => { setPixels(px); setCleanUrl(null); setUseClean(false); }, []);

  // Optional: detect the subject and place them on a clean white background.
  const cleanBackground = async () => {
    if (!rawSrc || !pixels) return;
    setBgBusy(true);
    try {
      const canvas = await cropToSquareCanvas(rawSrc, pixels);
      const plain = canvas.toDataURL('image/jpeg', 0.92);
      setOrigUrl(plain);
      const { cleanHeadshotToWhite } = await import('@/lib/headshotBg');
      const blob = await cleanHeadshotToWhite(plain, token);
      const url = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
      setCleanUrl(url); setUseClean(true);
    } catch (err) {
      console.warn('Headshot background clean-up failed:', err);
      toast.error('Background clean-up is unavailable right now — your original photo is kept.');
    } finally { setBgBusy(false); }
  };

  // Open the crop + clean dialog on the ALREADY-uploaded photo. Fetch it as a
  // same-origin data URL first so the canvas is not tainted (needed for the
  // white-background compositing and the final save).
  const editExisting = async () => {
    setBusy(true);
    try {
      const r = await axios.get(src, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const reader = new FileReader();
      reader.onload = () => { setRawSrc(reader.result); setCrop({ x: 0, y: 0 }); setZoom(1); setOrigUrl(null); setCleanUrl(null); setUseClean(false); };
      reader.readAsDataURL(r.data);
    } catch (err) {
      toast.error('Could not open the current photo for editing');
    } finally { setBusy(false); }
  };

  const confirmCrop = async () => {
    if (!rawSrc || !pixels) return;
    setBusy(true);
    try {
      let blob;
      if (useClean && cleanUrl) blob = await dataUrlToBlob(cleanUrl);
      else blob = await canvasToBlob(await cropToSquareCanvas(rawSrc, pixels));
      const fd = new FormData();
      fd.append('file', new File([blob], 'headshot.jpg', { type: 'image/jpeg' }));
      await axios.post(base, fd, { headers: { Authorization: `Bearer ${token}` } });
      setPresent(true); setBust(Date.now()); resetCropState();
      toast.success('Photo saved'); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save photo');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(base, { headers: { Authorization: `Bearer ${token}` } });
      setPresent(false); toast.success('Photo removed'); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove photo');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-4" data-testid="personnel-photo-field">
      <div className={`relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shrink-0 ${box}`}>
        {present ? (
          <img src={src} alt="Headshot" className="h-full w-full object-cover"
            data-testid="personnel-photo-preview" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-slate-300" data-testid="personnel-photo-empty">
            <UserRound size={size === 'sm' ? 18 : 30} />
            {size !== 'sm' && <span className="text-[10px] font-semibold text-slate-500 text-center leading-tight px-1">Photo Not Provided</span>}
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden" onChange={onFile} data-testid="personnel-photo-input" />
          <input ref={camRef} type="file" accept="image/*" capture="user"
            className="hidden" onChange={onFile} data-testid="personnel-photo-camera-input" />
          <button type="button" onClick={pick} disabled={busy} data-testid="personnel-photo-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#16324F] disabled:opacity-50">
            {present ? <RefreshCw size={14} /> : <Upload size={14} />}{present ? 'Replace' : 'Upload File'}
          </button>
          <button type="button" onClick={() => camRef.current?.click()} disabled={busy} data-testid="personnel-photo-camera-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#0E2A47] px-3 py-1.5 text-sm font-medium text-[#0E2A47] hover:bg-slate-50 disabled:opacity-50">
            <Camera size={14} /> Take Photo
          </button>
          {present && (
            <button type="button" onClick={editExisting} disabled={busy} data-testid="personnel-photo-edit-existing-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#1763B6] px-3 py-1.5 text-sm font-medium text-[#1763B6] hover:bg-blue-50 disabled:opacity-50">
              <Sparkles size={14} /> Adjust / Clean
            </button>
          )}
          {present && (
            <button type="button" onClick={remove} disabled={busy} data-testid="personnel-photo-remove-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
              <Trash2 size={14} /> Remove
            </button>
          )}
          <span className="text-[11px] text-slate-400">Square 1:1 · company-ID style</span>
        </div>
      )}

      {rawSrc && (
        <Dialog open onOpenChange={(o) => { if (!o) resetCropState(); }}>
          <DialogContent className="max-w-md" data-testid="personnel-photo-crop-dialog">
            <DialogHeader><DialogTitle>Crop headshot (1:1)</DialogTitle>
              <DialogDescription className="sr-only">Adjust and crop the headshot to a 1:1 square for the company ID, and optionally place the person on a clean white background.</DialogDescription>
            </DialogHeader>
            <div className="relative h-72 w-full bg-slate-900 rounded-md overflow-hidden">
              <Cropper image={rawSrc} crop={crop} zoom={zoom} aspect={1} cropShape="rect"
                showGrid onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <ZoomIn size={15} className="text-slate-400" />
              <input type="range" min={1} max={3} step={0.05} value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))} className="w-full" data-testid="personnel-photo-zoom" />
            </div>

            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-slate-600">
                  <div className="font-semibold text-[#0E2A47]">Professional white background <span className="font-normal text-slate-400">(optional)</span></div>
                  Detect the person and place them on a clean white background. Facial features are not changed.
                </div>
                <button type="button" onClick={cleanBackground} disabled={bgBusy || busy} data-testid="personnel-photo-cleanbg-btn"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1763B6] px-3 py-1.5 text-xs font-semibold text-[#1763B6] hover:bg-blue-50 disabled:opacity-50">
                  {bgBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{bgBusy ? 'Processing…' : 'Clean background'}
                </button>
              </div>
              {cleanUrl && origUrl && (
                <div className="mt-3" data-testid="personnel-photo-bg-compare">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setUseClean(false)} data-testid="personnel-photo-bg-keep-original"
                      className={`rounded-md border-2 p-1 ${!useClean ? 'border-[#1763B6]' : 'border-transparent'}`}>
                      <img src={origUrl} alt="Original" className="aspect-square w-full rounded object-cover" />
                      <div className="mt-1 text-[11px] font-semibold text-slate-600">Original {!useClean && '✓'}</div>
                    </button>
                    <button type="button" onClick={() => setUseClean(true)} data-testid="personnel-photo-bg-use-clean"
                      className={`rounded-md border-2 p-1 ${useClean ? 'border-[#1763B6]' : 'border-transparent'}`}>
                      <img src={cleanUrl} alt="White background" className="aspect-square w-full rounded object-cover" />
                      <div className="mt-1 text-[11px] font-semibold text-slate-600">White background {useClean && '✓'}</div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <button onClick={resetCropState} className="rounded-md border border-gray-200 px-4 py-2 text-sm">Cancel</button>
              <button onClick={confirmCrop} disabled={busy || bgBusy} data-testid="personnel-photo-crop-confirm"
                className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
                {busy ? 'Saving…' : 'Save Photo'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PersonnelPhotoField;
